// The Game Lite write descriptors, and the executor that performs them.
//
// Every player-visible write goes through here so it inherits the durable
// queue's guarantees: it survives a dropped connection, a backgrounded tab and
// a page reload, and it is retried until the server has it.
//
// Ids are DETERMINISTIC — one per (participant, wine, question). Re-answering
// supersedes the unsent write rather than queuing a second one, and a reload
// can't create a duplicate of something already queued.
//
// An answer is final on the server once it is stored (migration 254, TAS-451).
// Two things here follow from that: a write refused as `answer is final` is
// settled, not lost (isAnswerFinalRefusal), and a wine's rating is queued
// behind that wine's answers (queuedAnswerWriteIds), because the server
// refuses a first answer for a wine that is already rated.

import { callRpc, isRetryableError } from './rpc';
import { writeQueue } from './writeQueue';

export const answerWriteId = (participantId, wineId, questionId) =>
  `answer:${participantId}:${wineId}:${questionId}`;

export const finishWineWriteId = (participantId, wineId) =>
  `finish:${participantId}:${wineId}`;

export const completeWineWriteId = (participantId, wineId) =>
  `complete:${participantId}:${wineId}`;

/**
 * The ids of this wine's answer writes that are still in the queue, in any
 * state: waiting, in flight, backing off, parked, or held for a token. These
 * are what the wine's rating has to go after. Read from the id, which is the
 * one thing every queued answer has had since the queue shipped.
 */
export function queuedAnswerWriteIds(participantId, wineId, queue = writeQueue) {
  const prefix = answerWriteId(participantId, wineId, '');
  return queue
    .getPending()
    .map((it) => it.id)
    .filter((id) => typeof id === 'string' && id.startsWith(prefix));
}

// Priorities: an answer is the thing we must not lose, so it drains first; the
// score/completion writes recompute from the answers, so they are only correct
// once the answers are in.
export const PRIORITY = { answer: 10, finish: 5, complete: 5 };

export function buildAnswerWrite({ participantId, token, wineId, questionId, selected }) {
  return {
    id: answerWriteId(participantId, wineId, questionId),
    rpc: 'solo_submit_answer',
    args: {
      p_participant_id: participantId,
      p_token: token,
      p_wine_id: wineId,
      p_question_id: questionId,
      p_selected: Array.isArray(selected) ? selected : [selected],
    },
    meta: { kind: 'answer', wineId, questionId },
    priority: PRIORITY.answer,
  };
}

// The place reveal's write, and the only finish the phone sends. The server
// counts it as this wine's reveal only when the wine's rating is already
// recorded (migration 251), and a finish that arrives first is accepted,
// records nothing for the wine, and is never sent again. So it is queued
// `after` the rating: while that write is anywhere in the queue, this one
// waits behind it. With the rating already landed there is nothing to wait on.
export function buildFinishWineWrite({ participantId, token, wineId }) {
  return {
    id: finishWineWriteId(participantId, wineId),
    rpc: 'solo_finish_wine',
    args: { p_participant_id: participantId, p_token: token, p_wine_id: wineId },
    meta: { kind: 'finish', wineId },
    priority: PRIORITY.finish,
    after: completeWineWriteId(participantId, wineId),
  };
}

// The rating. Once it is on the server the wine is complete, and a FIRST
// answer for a complete wine is refused for good (migration 254). So the
// caller passes `after`: the ids of this wine's answers that have not left the
// queue yet (queuedAnswerWriteIds), and the rating waits behind every one of
// them. With all the answers already landed there is nothing to pass, and the
// descriptor is what it always was.
export function buildCompleteWineWrite({ participantId, token, wineId, rating, after = [] }) {
  const write = {
    id: completeWineWriteId(participantId, wineId),
    rpc: 'solo_complete_wine',
    args: {
      p_participant_id: participantId,
      p_token: token,
      p_wine_id: wineId,
      p_rating: rating,
    },
    meta: { kind: 'complete', wineId },
    priority: PRIORITY.complete,
  };
  if (Array.isArray(after) && after.length > 0) write.after = [...after];
  return write;
}

/** Error the queue reads as "never going to work" (see WriteQueue.setExecutor). */
class PermanentWriteError extends Error {
  constructor(message, code, { tokenRefused = false, refusedToken = null, settled = false } = {}) {
    super(message);
    this.name = 'PermanentWriteError';
    this.permanent = true;
    this.code = code;
    // The write itself was fine; this device's token was not. The queue parks
    // these to wait for a readmit instead of discarding a real answer.
    this.tokenRefused = tokenRefused;
    this.refusedToken = refusedToken;
    // The server already holds this player's answer to the question and kept
    // it. The queue lets the write go without counting it as lost.
    this.settled = settled;
  }
}

/**
 * "Not your game" — every solo write RPC raises 42501 with that phrase when the
 * participant id and access token don't match a row (migration 125). It is the
 * ONLY 42501 those functions raise, but match on the message anyway: solo_join
 * uses the same code for "this email is already on another device", which is a
 * genuine refusal and must not be parked.
 */
export function isTokenRefusal(error) {
  return String(error?.code || '') === '42501' && /not your game/i.test(String(error?.message || ''));
}

/**
 * "Answer is final" — solo_submit_answer raises 42501 with that phrase when a
 * DIFFERENT selection arrives for a question this player has already answered
 * (migration 254). The server kept the first answer and it still scores, so
 * the write is settled, not lost: no retry, no "we couldn't save" banner.
 *
 * It shares its code with two refusals that mean something else, which is why
 * the message is matched: `not your game` (the token; parked for a readmit)
 * and `wine is complete` (a first answer that arrived after the rating; a
 * real loss). A token refusal is never also this one: with both phrases in a
 * message the token reading wins, because parking keeps the answer and
 * settling lets it go.
 */
export function isAnswerFinalRefusal(error) {
  return (
    String(error?.code || '') === '42501' &&
    /answer is final/i.test(String(error?.message || '')) &&
    !isTokenRefusal(error)
  );
}

/**
 * Perform one queued descriptor. One attempt per call with a hard deadline —
 * the queue owns the backoff and park policy, so retrying here too would
 * multiply the delays.
 *
 * The token is resolved HERE, not at enqueue time. A descriptor written before
 * a readmit carries the token this device held then; sending that after the
 * readmit would be refused for a reason the player has already fixed. Same fix
 * apps/game applies in its persist executor (TAS-360).
 */
export async function executeWrite(item) {
  const args = withCurrentToken(item.args);
  const { data, error, status } = await callRpc(item.rpc, args, { retries: 0 });
  if (!error) return data;
  if (!isRetryableError(error, status)) {
    const tokenRefused = isTokenRefusal(error);
    throw new PermanentWriteError(error.message || 'Write refused', error.code, {
      tokenRefused,
      refusedToken: tokenRefused ? args.p_token ?? null : null,
      // Only an answer can be final. The same words from any other write would
      // be a refusal nobody has planned for, and those are reported.
      settled: item.rpc === 'solo_submit_answer' && isAnswerFinalRefusal(error),
    });
  }
  throw new Error(error.message || 'Write failed');
}

/** Swap in whatever token this device holds right now, if it holds one. */
function withCurrentToken(args) {
  if (!args || !('p_token' in args)) return args;
  const fresh = readToken();
  return fresh ? { ...args, p_token: fresh } : args;
}

// Injected rather than imported, to keep this module free of a dependency on
// the provider (which imports it). initSoloWrites supplies the real reader.
let readToken = () => null;

/** Wire the singleton queue up to the real executor and browser events. */
export function initSoloWrites({ tokenReader } = {}) {
  if (typeof tokenReader === 'function') readToken = tokenReader;
  writeQueue.setExecutor(executeWrite);
  writeQueue.setTokenReader(readToken);
  writeQueue.start();
  writeQueue.rehydrate();
}

/** Test seam — forget the injected reader between cases. */
export function __resetTokenReader() {
  readToken = () => null;
}

export { PermanentWriteError };
